import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getHoldings, refreshAndWait } from '../../data/portfolio';
import { getPrivate } from '../../data/private';
import NavChart from '../portfolio/NavChart';
import AllocationBreakdown from '../portfolio/AllocationBreakdown';
import PortfolioTabs from '../portfolio/PortfolioTabs';
import PortfolioHeader from '../portfolio/PortfolioHeader';
import ViewToggle from '../portfolio/ViewToggle';
import SectionSummary, { SectionCards } from '../portfolio/SectionSummary';
import useSummaryInputs from '../portfolio/useSummaryInputs';
import { Skeleton } from '../portfolio/Loading';
import { netDepositsIn, totalValue, viewChange } from '../portfolio/accounts';
import { combine, partsIn, SECTIONS, summaryParts, windowsAvailable } from '../portfolio/sections';
import { DEFAULT_TIMEFRAME, TIMEFRAMES } from '../portfolio/timeframes';
import { useT } from '../../i18n';

// The whole portfolio: the headline, one summary, and a box per section that
// opens its tab. Each section's detail lives on its own tab, so there is no
// account selector here any more.

const LOAD_STATE = { LOADING: 'loading', READY: 'ready', ERROR: 'error' };

export default function Holdings() {
    const { t, tn, fmt } = useT();
    const [state, setState] = useState(LOAD_STATE.LOADING);
    const [data, setData] = useState(null);
    // Null until read, and left null if it cannot be: see `summaryParts`.
    const [privateList, setPrivateList] = useState(null);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState(null);
    const [windowKey, setWindowKey] = useState('all');
    const [timeframe, setTimeframe] = useState(DEFAULT_TIMEFRAME);
    const { inputs, setInput } = useSummaryInputs();

    const load = useCallback(async ({ refresh = false, previousAsOf = null } = {}) => {
        if (refresh) setRefreshing(true);
        setRefreshError(null);
        try {
            // A forced rebuild outlives the request that asked for it, so it is
            // waited for rather than awaited. See `refreshAndWait`.
            const result = refresh
                ? await refreshAndWait(getHoldings, { previousAsOf })
                : await getHoldings();
            setData(result);
            setState(LOAD_STATE.READY);
        } catch (err) {
            // A failed REFRESH must not replace a page that is already showing
            // good data with an error screen — the data on it is still true,
            // and the only thing that went wrong is that it is not newer.
            if (refresh) setRefreshError(err?.message ?? null);
            else {
                setError(err);
                setState(LOAD_STATE.ERROR);
            }
        } finally {
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        // StrictMode double-invokes effects in development. The flag stops a
        // late response from a discarded mount overwriting the live one.
        let cancelled = false;
        getHoldings()
            .then((result) => {
                if (cancelled) return;
                setData(result);
                setState(LOAD_STATE.READY);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err);
                setState(LOAD_STATE.ERROR);
            });
        getPrivate()
            .then((result) => { if (!cancelled) setPrivateList(result?.investments ?? []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const accounts = useMemo(() => data?.accounts ?? [], [data]);
    const parts = useMemo(
        () => summaryParts(accounts, { inputs, privateList }),
        [accounts, inputs, privateList],
    );
    // Each box at the overview's window where its section can answer it, and
    // at All where it cannot.
    const sections = useMemo(() => SECTIONS.map((section) => {
        const own = partsIn(parts, section.key);
        const key = windowsAvailable(own).some((w) => w.key === windowKey) ? windowKey : 'all';
        return { section, figures: combine(own, key) };
    }).filter((s) => s.figures), [parts, windowKey]);

    const navHistory = data?.navHistory ?? [];
    const change = useMemo(() => viewChange(accounts, timeframe), [accounts, timeframe]);
    const netDeposits = useMemo(() => netDepositsIn(accounts, timeframe), [accounts, timeframe]);
    const total = totalValue(accounts) ?? data?.totalValue ?? null;

    return (
        <section className="holdings__sec">
            <div className="wrapper">
                <PortfolioHeader
                    title={t('holdings.title')}
                    status={data?.asOf && t('common.asOf', { when: fmt.dateTime(data.asOf) })}
                    onRefresh={() => load({ refresh: true, previousAsOf: data?.asOf })}
                    refreshing={refreshing}
                />

                <PortfolioTabs />

                {state === LOAD_STATE.LOADING && <Skeleton rows={10} />}

                {state === LOAD_STATE.ERROR && (
                    <p className="holdings__error" role="alert">
                        {error?.message || t('holdings.error')}
                    </p>
                )}

                {state === LOAD_STATE.READY && data && (
                    <>
                        {total && (
                            <div className="holdings__total">
                                <span className="holdings__total_value">{fmt.number(total)}</span>
                                <span className="holdings__total_currency">{data.baseCurrency}</span>
                            </div>
                        )}

                        {refreshError && (
                            <p className="holdings__error" role="alert">
                                {t('holdings.refreshFailed', { reason: refreshError })}
                            </p>
                        )}

                        {data.warnings?.length > 0 && (
                            <Link to="/holdings/notes" className="holdings__notices">
                                {tn('holdings.notices', data.warnings.length)}
                            </Link>
                        )}

                        <SectionSummary
                            parts={parts}
                            windowKey={windowKey}
                            onWindow={setWindowKey}
                            inputs={inputs}
                            onInput={setInput}
                        />

                        {sections.length > 0 && <SectionCards sections={sections} />}

                        <div className="holdings__timeframe">
                            <ViewToggle
                                ariaLabel={t('timeframe.aria')}
                                options={TIMEFRAMES.map((f) => [f.key, t(f.labelKey)])}
                                value={timeframe}
                                onChange={setTimeframe}
                            />
                        </div>

                        <NavChart
                            navHistory={navHistory}
                            currency={data.baseCurrency}
                            timeframe={timeframe}
                            netDeposits={netDeposits}
                            windowChange={change}
                        />

                        {data.positions?.length > 0 && (
                            <AllocationBreakdown positions={data.positions} baseCurrency={data.baseCurrency} />
                        )}

                        {/* No asset list: each tab lists its own, and Medora's
                            holdings open from its row on the Private tab. */}
                        {!(data.positions?.length > 0) && (
                            <div className="holdings__empty">
                                <p className="desc">
                                    {t('holdings.emptyBefore')}{' '}
                                    <Link to="/holdings/settings">{t('holdings.emptyLink')}</Link>
                                    {t('holdings.emptyAfter')}
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}
